import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { formatDateAndTime } from '@racemap/utilities/formattings/times';
import { type FC, type ReactNode, useEffect, useRef } from 'react';
import { useInfiniteScroll } from 'src/lib/customHooks/useInfiniteScroll';
import { EventLogo } from '../BasicComponents/EventLogo';
import { KeyCap, type KeyHint, KeyHintsFooter } from '../BasicComponents/Hotkeys';
import { IconClockRotateLeft } from '../BasicComponents/Icons';
import { LoadingIndicator } from '../BasicComponents/LoadingIndicator';
import { PaymentStateBadge, RunningBadge } from '../EventInfoBadges';
import { highlightRanges } from './highlightRanges';
import type { EventSearchItem, SearchGroup, SearchItem } from './useSearchResults';

const ResultHints: Array<KeyHint> = [
  { keys: ['↑', '↓'], label: 'move' },
  { keys: ['⏎'], label: 'open' },
  { keys: ['esc'], label: 'close' },
];
const EmptyHints: Array<KeyHint> = [{ keys: ['esc'], label: 'close' }];

/** Ties a row to the input's aria-activedescendant. */
export const optionId = (listboxId: string, index: number) => `${listboxId}-option-${index}`;

interface Props {
  groups: Array<SearchGroup>;
  query: string;
  activeIndex: number;
  isLoading: boolean;
  hasError: boolean;
  onRetry: () => void;
  hasNextPage: boolean;
  onLoadNextPage: () => void;
  onSelect: (item: SearchItem) => void;
  onHover: (index: number) => void;
  listboxId: string;
  fullWidth?: boolean;
}

export const SearchPanel: FC<Props> = ({
  groups,
  query,
  activeIndex,
  isLoading,
  hasError,
  onRetry,
  hasNextPage,
  onLoadNextPage,
  onSelect,
  onHover,
  listboxId,
  fullWidth = false,
}) => {
  const sentinelRef = useInfiniteScroll({
    onLoadMore: onLoadNextPage,
    hasMore: hasNextPage,
    isLoading,
  });
  const isEmpty = groups.every((group) => group.items.length === 0);
  // Runs across all groups: this is the index space the keyboard cursor moves in.
  let renderIndex = -1;

  return (
    <Container fullWidth={fullWidth}>
      {/* biome-ignore lint/a11y/useSemanticElements: a <select> cannot hold rich rows, grouped
          categories or a footer, and this listbox is driven by a separate combobox input */}
      <Scroller role="listbox" id={listboxId} aria-label="Search results">
        {hasError && (
          <ErrorState>
            <div>Search failed.</div>
            <LoadingIndicator isLoading={false} borderless onReload={onRetry} />
          </ErrorState>
        )}
        {!hasError && isEmpty && isLoading && <LoadingIndicator borderless text="Searching..." />}
        {!hasError && isEmpty && !isLoading && <NoResults>No results found.</NoResults>}

        {groups.map((group, groupIndex) => (
          // biome-ignore lint/a11y/useSemanticElements: a <fieldset> is not valid inside a listbox
          <div key={group.key} role="group" aria-label={group.title}>
            <GroupTitle aria-hidden withSeparator={groupIndex > 0}>
              {group.title}
            </GroupTitle>
            {group.items.map((item) => {
              renderIndex += 1;
              const index = renderIndex;

              return (
                <Row
                  key={item.key}
                  id={optionId(listboxId, index)}
                  item={item}
                  query={query}
                  isActive={index === activeIndex}
                  onSelect={() => onSelect(item)}
                  onHover={() => onHover(index)}
                />
              );
            })}
          </div>
        ))}

        {!isEmpty && isLoading && <LoadingIndicator borderless />}
        <div ref={sentinelRef} />
      </Scroller>

      <KeyHintsFooter hints={isEmpty ? EmptyHints : ResultHints} />
    </Container>
  );
};

interface RowProps {
  id: string;
  item: SearchItem;
  query: string;
  isActive: boolean;
  onSelect: () => void;
  onHover: () => void;
}

const Row: FC<RowProps> = ({ id, item, query, isActive, onSelect, onHover }) => {
  const rowRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isActive) rowRef.current?.scrollIntoView({ block: 'nearest' });
  }, [isActive]);

  return (
    <RowButton
      ref={rowRef}
      type="button"
      id={id}
      // biome-ignore lint/a11y/useSemanticElements: an <option> renders as plain text, not the
      // logo, badges and marked-up name a result row needs
      role="option"
      aria-selected={isActive}
      isActive={isActive}
      onMouseMove={onHover}
      onClick={onSelect}
    >
      {item.kind === 'event' ? (
        <EventRowContent item={item} query={query} />
      ) : (
        <>
          <RecentIcon>
            <IconClockRotateLeft />
          </RecentIcon>
          <Details>
            <Name>{item.query}</Name>
          </Details>
        </>
      )}
      {isActive && (
        <Affordance>
          <KeyCap>⏎</KeyCap>
        </Affordance>
      )}
    </RowButton>
  );
};

const EventRowContent: FC<{ item: EventSearchItem; query: string }> = ({ item, query }) => {
  const { event } = item;

  return (
    <>
      <EventLogo event={event} size={42} rounded={4} />
      <Details>
        <Name>
          <Highlighted text={event.name || event.id} query={query} />
        </Name>
        <SubLine>
          {/* The template time on purpose, not currentEventTimes: shifting a repeating event to
              its next occurrence is for spectators, the back office wants the original. */}
          {[formatDateAndTime(event.startTime, true), event.location]
            .filter((part) => part != null && part !== '')
            .map((part, index) => (
              // The location is searchable too, so it carries marks like the name does.
              <span key={part}>
                {index > 0 && ' · '}
                <Highlighted text={part} query={query} />
              </span>
            ))}
        </SubLine>
        <Badges>
          <RunningBadge event={event} />
          <PaymentStateBadge event={event} />
        </Badges>
      </Details>
    </>
  );
};

const Highlighted: FC<{ text: string; query: string }> = ({ text, query }) => {
  const ranges = highlightRanges(text, query);
  if (ranges.length === 0) return <>{text}</>;

  const parts: Array<ReactNode> = [];
  let cursor = 0;
  for (const [start, end] of ranges) {
    if (start > cursor) parts.push(text.slice(cursor, start));
    parts.push(<Mark key={start}>{text.slice(start, end)}</Mark>);
    cursor = end;
  }
  if (cursor < text.length) parts.push(text.slice(cursor));

  return <>{parts}</>;
};

// fullWidth means the panel sits inside the mobile sheet, which already provides the frame.
const Container = styled.div<{ fullWidth: boolean }>`
  display: flex;
  flex-direction: column;
  min-height: 0;
  width: ${({ fullWidth }) => (fullWidth ? '100%' : '600px')};
  max-width: 100vw;
  background: #fff;
  overflow: hidden;
  ${({ fullWidth }) =>
    fullWidth
      ? ''
      : `border: 1px solid ${RacemapColors.CloudBlue};
         border-radius: 6px;
         box-shadow: 0 14px 40px rgba(16, 32, 48, 0.24);`}
`;

// Unpadded, so rows run edge to edge and the active row's accent bar sits on the panel border.
const Scroller = styled.div`
  overflow-y: auto;
  min-height: 0;
  max-height: min(60vh, 420px);
`;

const GroupTitle = styled.div<{ withSeparator: boolean }>`
  padding: 11px 14px 5px;
  border-top: ${({ withSeparator }) =>
    withSeparator ? `1px solid ${RacemapColors.LightGray}` : 'none'};
  color: ${RacemapColors.DarkGray};
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.7px;
  text-transform: uppercase;
`;

const RowButton = styled.button<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 11px;
  width: 100%;
  padding: 9px 14px;
  border: none;
  text-align: left;
  cursor: pointer;
  background: ${({ isActive }) => (isActive ? RacemapColors.LightBlue : 'transparent')};
  box-shadow: ${({ isActive }) => (isActive ? `inset 3px 0 0 ${RacemapColors.PaleBlue}` : 'none')};
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  flex: 1;
`;

const Name = styled.div`
  font-size: 14.5px;
  font-weight: 600;
  color: ${RacemapColors.DarkBlue};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Mark = styled.mark`
  background: ${RacemapColors.LightYellow};
  color: inherit;
`;

const SubLine = styled.div`
  color: ${RacemapColors.DarkGray};
  font-size: 12.5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// The shared badges are sized for page content. Scoped here so their other uses are untouched;
// the icons follow the font size on their own, being sized in em.
const Badges = styled.div`
  display: flex;
  gap: 5px;
  margin-top: 4px;

  /* The badges are shared components, so their Tags cannot take a styles prop from here. Matched
     structurally rather than by .ant-tag, which is the class name that shifts between majors. */
  > span {
    /* antd spaces tags with a trailing margin; the flex gap above replaces it. */
    margin-inline-end: 0;
    padding: 0 5px;
    border-radius: 3px;
    font-size: 9.5px;
    font-weight: 600;
    letter-spacing: 0.4px;
    line-height: 16px;
  }
`;

// Occupies the same 42px column as an event logo, so recent rows line up with result rows.
const RecentIcon = styled.div`
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 42px;
  color: ${RacemapColors.DarkGray};
`;

const Affordance = styled.div`
  display: flex;
  flex: none;
  align-items: center;
  align-self: center;
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-start;
  padding: 11px 14px;
  color: ${RacemapColors.DarkDarkGray};
`;

const NoResults = styled.div`
  padding: 11px 14px;
  color: ${RacemapColors.DarkDarkGray};
`;
